//Angular imports
import { NgModule, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgOptimizedImage } from '@angular/common';
import { HttpErrorInterceptor } from './helpers/interceptors/http-error.interceptor';
import { SpinnerInterceptor } from './helpers/interceptors/spinner.interceptor';
import { AuthInterceptor } from './helpers/interceptors/auth-interceptor';
import { Router } from '@angular/router';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { IPublicClientApplication, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
//Application imports
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './components/shared.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/layout/header/header.component';
import { FooterComponent } from './components/layout/footer/footer.component';
import { AddProjectTileComponent } from './components/add-project-tile/add-project-tile.component';
import { ProjectTileComponent } from './components/project-tile/project-tile.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { LoaderComponent } from './components/loader/loader.component';
import { ProjectListComponent } from './components/project-list/project-list.component';
import { FilterDataComponent } from './components/filters/filter-data.component';
import { WelcomeMessagePopupComponent } from './components/dialogs/welcome-message-popup/welcome-message-popup.component';
import { ProjectConfigMenuComponent } from './components/dialogs/project-config-menu/project-config-menu.component';
import { CreateProjectDialogComponent } from './components/dialogs/create-project-dialog/create-project-dialog.component';
import { ModuleConfigurationDialogComponent } from './components/dialogs/module-configuration-dialog/module-configuration-dialog.component';
import { loginRequest, msalConfig, protectedResources } from './auth/auth-config';
import { SpinnerService } from './api/spinner.service';
import { ProjectConfigurationComponent } from './pages/project-configuration/project-configuration.component';
import { ProjectConfigurationSummaryComponent } from './pages/project-configuration-summary/project-configuration-summary.component';
import { ProjectBucketSliderComponent } from './components/project-bucket-slider/project-bucket-slider.component';
import { NotificationCenterComponent } from './pages/notification-center/notification-center.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { SignoutOidcComponent } from './pages/signout-oidc/signout-oidc.component';
import { AuthService } from './auth/auth.service';
import { NotFoundComponent } from './pages/not-found/not-found.component';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(protectedResources.graphMe.endpoint, protectedResources.graphMe.scopes);
  protectedResourceMap.set(protectedResources.welcome.endpoint, protectedResources.welcome.scopes);
  protectedResourceMap.set(protectedResources.profile.endpoint, protectedResources.profile.scopes);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    WelcomeMessagePopupComponent,
    FooterComponent,
    AddProjectTileComponent,
    ProjectTileComponent,
    DashboardComponent,
    ProfileComponent,
    BreadcrumbComponent,
    ProfileComponent,
    ProjectConfigMenuComponent,
    BreadcrumbComponent,
    CreateProjectDialogComponent,
    ProjectListComponent,
    FilterDataComponent,
    LoaderComponent,
    ModuleConfigurationDialogComponent,
    ProjectConfigurationComponent,
    ProjectConfigurationSummaryComponent,
    ProjectBucketSliderComponent,
    NotificationCenterComponent,
    UnauthorizedComponent,
    SignoutOidcComponent,
    NotFoundComponent
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    //Angular modules
    BrowserModule,
    BrowserAnimationsModule,
    //Application modules
    AppRoutingModule,
    NgOptimizedImage,
    SharedModule,
    MsalModule,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: function (router: Router, loaderService: SpinnerService, authService: AuthService) {
        return new AuthInterceptor(router, loaderService, authService);
      },
      multi: true,
      deps: [Router, SpinnerService, AuthService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
